'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { Locale, defaultLocale, loadTranslations, locales, preloadTranslations } from './i18n';

type LanguageContextType = {
  locale: Locale;
  translations: Record<string, any>;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocale] = useState<Locale>(defaultLocale);
  const [translations, setTranslations] = useState<Record<string, any>>({});
  const [isTranslationsLoaded, setIsTranslationsLoaded] = useState(false);

  // Load all translations on initial mount
  useEffect(() => {
    const loadAllTranslations = async () => {
      try {
        const allTranslations = await preloadTranslations();
        setTranslations(allTranslations[locale]);
        setIsTranslationsLoaded(true);
      } catch (error) {
        console.error('Failed to preload translations:', error);
      }
    };
    
    loadAllTranslations();
  }, []);

  // Update translations when locale changes
  useEffect(() => {
    if (isTranslationsLoaded) {
      const loadAndSetTranslations = async () => {
        const loadedTranslations = await loadTranslations(locale);
        setTranslations(loadedTranslations);
      };
      
      loadAndSetTranslations();
    }
  }, [locale, isTranslationsLoaded]);

  const changeLocale = (newLocale: Locale) => {
    if (locales.includes(newLocale)) {
      setLocale(newLocale);
      // Save to localStorage
      localStorage.setItem('preferredLanguage', newLocale);
      // Also save to cookie for middleware access
    }
  };

  useEffect(() => {
    // Check localStorage or cookies for saved preference
    const savedLocale = localStorage.getItem('preferredLanguage') as Locale | null || defaultLocale;
    
    if (savedLocale && locales.includes(savedLocale)) {
      setLocale(savedLocale);
    }
  }, []);

  // Translation function
  const t = (key: string): string => {
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      if (!value || !value[k]) return key; // Return key if translation not found
      value = value[k];
    }
    
    return typeof value === 'string' ? value : key;
  };

  // If translations aren't loaded yet, show a loading indicator or fallback
  if (!isTranslationsLoaded) {
    return <div className="min-h-screen flex items-center justify-center">Loading translations...</div>;
  }

  return (
    <LanguageContext.Provider value={{ locale, translations, setLocale: changeLocale, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

// Hook to use language context
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

// Format translation string with variables
export function formatMessage(message: string, variables: Record<string, any>): string {
  return message.replace(/{(\w+)}/g, (match, key) => {
    return variables[key] !== undefined ? variables[key] : match;
  });
} 